import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  defaultModelLabUiPreferences,
  defaultSlateUiPreferences,
  type ModelLabUiPreferences,
  type SlateUiPreferences,
  type UiPreferencesPatch,
  type UiPreferencesV1,
} from "./api";
import { useAuth } from "./auth";
import { config } from "./config";

const LEGACY_SLATE_KEY = "pb_tweaks";

type LocalBundle = {
  slate: SlateUiPreferences;
  model_lab: ModelLabUiPreferences;
};

function cacheKey(leagueId: string): string {
  return `predabet.ui_prefs.${leagueId}.v1`;
}

function mergeCachedModelLab(raw: unknown): ModelLabUiPreferences {
  const d = defaultModelLabUiPreferences();
  if (!raw || typeof raw !== "object") return d;
  const r = raw as Record<string, unknown>;
  return {
    show_run_history:
      typeof r.show_run_history === "boolean"
        ? r.show_run_history
        : d.show_run_history,
    compact_sliders:
      typeof r.compact_sliders === "boolean"
        ? r.compact_sliders
        : d.compact_sliders,
  };
}

function readLocalBundle(leagueId: string): LocalBundle | null {
  try {
    const raw = localStorage.getItem(cacheKey(leagueId));
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;
    if (typeof p !== "object" || p === null) return null;
    const rec = p as Record<string, unknown>;
    const slate = rec.slate as unknown;
    const model_lab = rec.model_lab as unknown;
    if (
      typeof slate !== "object" ||
      slate === null ||
      typeof model_lab !== "object" ||
      model_lab === null
    ) {
      return null;
    }
    return {
      slate: {
        ...defaultSlateUiPreferences(),
        ...(slate as Partial<SlateUiPreferences>),
      },
      model_lab: mergeCachedModelLab(model_lab),
    };
  } catch {
    return null;
  }
}

function writeLocalBundle(leagueId: string, bundle: LocalBundle): void {
  try {
    localStorage.setItem(cacheKey(leagueId), JSON.stringify(bundle));
  } catch {
    /* ignore */
  }
}

/** Migrate pre-v1 slate tweaks (camelCase) into slate prefs. */
function readLegacySlateTweaks(): Partial<SlateUiPreferences> | null {
  try {
    const raw = localStorage.getItem(LEGACY_SLATE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<SlateUiPreferences> = {};
    if (
      p.probStyle === "bar" ||
      p.probStyle === "number" ||
      p.probStyle === "gauge"
    ) {
      out.prob_style = p.probStyle;
    }
    if (typeof p.groupByDate === "boolean") out.group_by_date = p.groupByDate;
    if (typeof p.hideEmptyMarket === "boolean")
      out.hide_empty_market = p.hideEmptyMarket;
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

function initialBundle(leagueId: string): LocalBundle {
  const fromCache = readLocalBundle(leagueId);
  const legacy = readLegacySlateTweaks();
  const slate = {
    ...defaultSlateUiPreferences(),
    ...(legacy ?? {}),
    ...(fromCache?.slate ?? {}),
  };
  const model_lab = {
    ...defaultModelLabUiPreferences(),
    ...(fromCache?.model_lab ?? {}),
  };
  return { slate, model_lab };
}

/**
 * Merge a server ui_preferences document into local state. The API may omit
 * `show_your_model` / `show_delta_pp` (they normalize to false); preserve the
 * local optimistic or cached column toggles unless this response is for a PUT
 * that explicitly included them.
 */
function mergeServerUiPreferences(
  prev: LocalBundle,
  doc: UiPreferencesV1,
  appliedPatch?: UiPreferencesPatch,
): LocalBundle {
  const slatePatch = appliedPatch?.slate;
  const modelLabPatch = appliedPatch?.model_lab;
  return {
    slate: {
      ...doc.slate,
      show_your_model:
        slatePatch && "show_your_model" in slatePatch
          ? slatePatch.show_your_model!
          : prev.slate.show_your_model,
      show_delta_pp:
        slatePatch && "show_delta_pp" in slatePatch
          ? slatePatch.show_delta_pp!
          : prev.slate.show_delta_pp,
    },
    model_lab: modelLabPatch
      ? { ...doc.model_lab, ...modelLabPatch }
      : doc.model_lab,
  };
}

type DisplayPreferencesContextValue = {
  slate: SlateUiPreferences;
  modelLab: ModelLabUiPreferences;
  updateSlate: (patch: Partial<SlateUiPreferences>) => void;
  updateModelLab: (patch: Partial<ModelLabUiPreferences>) => void;
  /** True after local hydration; remote fetch finished or skipped. */
  remoteHydrated: boolean;
};

const DisplayPreferencesContext =
  createContext<DisplayPreferencesContextValue | null>(null);

const SAVE_DEBOUNCE_MS = 450;

export function DisplayPreferencesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useAuth();
  const idToken = user?.idToken ?? null;
  const leagueId = config.leagueId;
  const [bundle, setBundle] = useState<LocalBundle>(() =>
    initialBundle(leagueId),
  );
  const [remoteHydrated, setRemoteHydrated] = useState(false);

  const pendingPut = useRef<UiPreferencesPatch>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqSeq = useRef(0);
  const lastFetchedToken = useRef<string | null>(null);
  const lastTokenRef = useRef<string | null>(idToken);

  const loggedOutBundle = useMemo(() => initialBundle(leagueId), [leagueId]);
  const bundleForCtx = idToken ? bundle : loggedOutBundle;
  const remoteHydratedForCtx = idToken ? remoteHydrated : true;

  const flushSave = useCallback(async () => {
    const token = idToken;
    const patch = pendingPut.current;
    pendingPut.current = {};
    if (!token || (!patch.slate && !patch.model_lab)) return;

    const mySeq = ++reqSeq.current;
    try {
      const doc = await api.setUiPreferences(patch, { idToken: token });
      if (mySeq !== reqSeq.current) return;
      setBundle((prev) => {
        const next = mergeServerUiPreferences(prev, doc, patch);
        writeLocalBundle(leagueId, next);
        return next;
      });
    } catch (err) {
      console.warn("Failed to save UI preferences", err as Error);
    }
  }, [idToken, leagueId]);

  const scheduleSave = useCallback(
    (fragment: UiPreferencesPatch) => {
      if (fragment.slate) {
        pendingPut.current.slate = {
          ...pendingPut.current.slate,
          ...fragment.slate,
        };
      }
      if (fragment.model_lab) {
        pendingPut.current.model_lab = {
          ...pendingPut.current.model_lab,
          ...fragment.model_lab,
        };
      }
      if (!idToken) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        void flushSave();
      }, SAVE_DEBOUNCE_MS);
    },
    [idToken, flushSave],
  );

  useEffect(() => {
    // If we are about to change auth/token state, flush any pending writes using the *previous*
    // token. This prevents "last click" preferences (e.g. gauge) from being dropped during
    // sign-out / token refresh / re-auth.
    const prevToken = lastTokenRef.current;
    const hasPending =
      !!pendingPut.current.slate || !!pendingPut.current.model_lab;
    if (prevToken && hasPending) {
      void api
        .setUiPreferences(pendingPut.current, { idToken: prevToken })
        .catch((err) => {
          console.warn(
            "Failed to flush UI preferences before auth change",
            err as Error,
          );
        });
      pendingPut.current = {};
    }
    lastTokenRef.current = idToken;

    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    reqSeq.current += 1;
    if (!idToken) {
      lastFetchedToken.current = null;
      return;
    }
    if (lastFetchedToken.current === idToken) return;
    lastFetchedToken.current = idToken;
    setRemoteHydrated(false);
    let cancelled = false;
    void api
      .getUiPreferences({ idToken })
      .then((doc: UiPreferencesV1) => {
        if (cancelled) return;
        setBundle((prev) => {
          const next = mergeServerUiPreferences(prev, doc);
          writeLocalBundle(leagueId, next);
          return next;
        });
      })
      .catch((err) => {
        console.warn(
          "Failed to load UI preferences; using local cache",
          err as Error,
        );
      })
      .finally(() => {
        if (!cancelled) setRemoteHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, leagueId]);

  const updateSlate = useCallback(
    (patch: Partial<SlateUiPreferences>) => {
      setBundle((prev) => {
        const next = { ...prev, slate: { ...prev.slate, ...patch } };
        writeLocalBundle(leagueId, next);
        return next;
      });
      scheduleSave({ slate: patch });
    },
    [leagueId, scheduleSave],
  );

  const updateModelLab = useCallback(
    (patch: Partial<ModelLabUiPreferences>) => {
      setBundle((prev) => {
        const next = { ...prev, model_lab: { ...prev.model_lab, ...patch } };
        writeLocalBundle(leagueId, next);
        return next;
      });
      scheduleSave({ model_lab: patch });
    },
    [leagueId, scheduleSave],
  );

  const value = useMemo(
    () => ({
      slate: bundleForCtx.slate,
      modelLab: bundleForCtx.model_lab,
      updateSlate,
      updateModelLab,
      remoteHydrated: remoteHydratedForCtx,
    }),
    [
      bundleForCtx.slate,
      bundleForCtx.model_lab,
      updateSlate,
      updateModelLab,
      remoteHydratedForCtx,
    ],
  );

  return (
    <DisplayPreferencesContext.Provider value={value}>
      {children}
    </DisplayPreferencesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDisplayPreferences(): DisplayPreferencesContextValue {
  const ctx = useContext(DisplayPreferencesContext);
  if (!ctx) {
    throw new Error(
      "useDisplayPreferences must be used inside <DisplayPreferencesProvider>",
    );
  }
  return ctx;
}
